import React, { useState, useEffect, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import SweetAlert from "sweetalert2";
import { bulkPolicyReset, saveValidationData, clearDiscountData, saveBulkFileUrl, saveBulkQuotationNumber, bulkPolicy, saveDiscountData, updateDiscountedRecords, saveAddonData } from "../../../../redux/bulkPolicy/action";
import FileUploadComponent from "../../../common/FileUpload";
import { validateBulkDocuments, getQuotationById, getWalletBalance } from "../../../../api";
import convertToRupees from "../../../../utilities/convertToRupees";
import { ExportToCsv } from "export-to-csv";
import { getFromLocalStorage } from "../../../../hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";
import { SALES_MANAGER } from "../../../../constant";
import BulkDocumentSuccess from "./bulk-document-success";

const BulkDocumentReview = ({ activeCompany, handleBackClick, onValidationSuccess, fromQuotation,userData,balance, quotation_id }) => {
  // Get data from Redux
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const policyData = useSelector((content) => content.bulkPolicy);
  const [isProcessingSubmit, setIsProcessingSubmit] = useState(false);

  // Form handling
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    trigger,
    getValues,
    reset,
    watch,
  } = useForm();

  // Watch for fileName changes to toggle Add/Edit button
  const fileName = watch("fileName");
  
  // Check if fileName is actually set and not empty, and it's not the default image
  const hasFileUploaded = fileName && 
    typeof fileName === 'string' && 
    fileName.trim().length > 0 && 
    !fileName.includes('file-upload.png');

  // State management
  const [fileUrl, setFileUrl] = useState("");  // Start with empty, not image
  const [fileUploadKey, setFileUploadKey] = useState(0); // Force re-render of FileUploadComponent
  const [failureData, setFailureData] = useState([]);
  const [successData, setSuccessData] = useState([]);
  const [validationResponse, setValidationResponse] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [loadingQuotation, setLoadingQuotation] = useState(false);

  // Extract values from Redux or use default empty strings
  const selectedCompany = policyData?.generateQuote?.company_id || "";
  const selectedPolicyType = policyData?.generateQuote?.policyType || "";
  const selectedDocumentVariant = policyData?.generateQuote?.documentVariant || "";
  const selectedBulkUpload = policyData?.generateQuote?.bulkUpload || "";
  console.log("📊 Bulk Policy Data from Redux:", policyData);
  console.log("📋 Selected Values:", {
    company_id: selectedCompany,
    policyType: selectedPolicyType,
    documentVariant: selectedDocumentVariant,
    bulkUpload: selectedBulkUpload
  });
  // Get role and user info
  const ROLE = JSON.parse(localStorage.getItem("ROLE"));
  console.log("User Data",userData);
  const role_key = ROLE?.key;
  const role_name = ROLE?.name;
  const role_id = ROLE?.role_id;
  const user  = role_key == "SALES_MANAGER" ? userData : getFromLocalStorage("user");
  // const user =
  //   role_key === SALES_MANAGER
  //     ? JSON.parse(localStorage.getItem("user"))
  //     : JSON.parse(localStorage.getItem("user"));

  const timestamp = new Date().toISOString().replace(/:/g, "-");

  // Load quotation data if coming from quotations page
  useEffect(() => {
    console.log("🔍 BulkDocumentReview - Props received:", { fromQuotation, quotation_id });
    
    const loadQuotationData = async () => {
      if (quotation_id && fromQuotation) {
        setLoadingQuotation(true);
        try {
          const response = await getQuotationById(quotation_id);
          if (response?.status === 200) {
            // The API returns data wrapped in documentDetails array
            const apiResult = response?.data?.result;
            let quotationData = null;
            
            // Check if result is wrapped in documentDetails array
            if (apiResult?.documentDetails && Array.isArray(apiResult.documentDetails)) {
              quotationData = apiResult.documentDetails[0]; // Get first item from array
            } else {
              quotationData = apiResult; // Use directly if not wrapped
            }
            
            console.log("🔍 Full API Response:", response?.data?.result);
            console.log("🔍 Extracted quotationData:", quotationData);
            
            // Check if it's a bulk quotation
            if (quotationData?.is_bulk && quotationData?.bulk_url) {
              const bulkRecords = quotationData?.document_data?.bulk_records || [];
              const documentDetails = quotationData?.document_data?.documentDetails || {};
              
              console.log("🔍 bulkRecords:", bulkRecords);
              console.log("🔍 documentDetails:", documentDetails);
              
              // Extract initial fields from document_data.documentDetails
              const company_id = documentDetails.company_id;
              const type_id = documentDetails.type_id;
              const document_variant = documentDetails.document_variant;
              const bulk_upload = documentDetails.bulk_upload;
              
              console.log("� Extracted fields:", {
                company_id,
                type_id,
                document_variant,
                bulk_upload
              });
              
              // Restore bulk flow initial fields (company_id, type_id, etc.)
              if (company_id || type_id) {
                const bulkPolicyPayload = {
                  companyId: company_id || '',
                  typeId: type_id || '',
                  documentVariant: document_variant || '',
                  generateQuote: {
                    company_id: company_id || '',
                    policyType: type_id || '',
                    documentVariant: document_variant || '',
                    bulkUpload: bulk_upload || 'yes',
                  }
                };
                
                console.log("🚀 Dispatching bulkPolicy with:", bulkPolicyPayload);
                dispatch(bulkPolicy(bulkPolicyPayload));
              }
              
              // Pre-fill the file URL from the quotation
              setFileUrl(quotationData.bulk_url);
              setValue("fileName", quotationData.bulk_url);
              
              // If the quotation has validation data, load it into Redux
              if (bulkRecords.length > 0) {
                // Save the bulk file URL first
                dispatch(saveBulkFileUrl(quotationData.bulk_url));
                
                // Save the quotation number to Redux so it can be used during payment
                if (quotationData.quotation_number) {
                  dispatch(saveBulkQuotationNumber(quotationData.quotation_number));
                }
                
                // Map the bulk_records to match the expected successLog structure
                // The structure from quotation already matches what we need
                const mappedRecords = bulkRecords.map((record, index) => ({
                  ...record,
                  // Ensure all required fields are present
                  type_id: record.type_id || 7,
                  record_index: record.record_index || (index + 1),
                  proposer: record.proposer || {},
                  travellers: record.travellers || [],
                  nominees: record.nominees || [],
                  documentDetails: record.documentDetails || {},
                  planDetails: record.planDetails || {},
                  geographical_cover_id: record.geographical_cover_id || 10,
                  geographical_cover_name: record.geographical_cover_name || "",
                  discount: record.discount || null,
                }));
                
                // Calculate total premium from all records
                const calculatedTotalPremium = mappedRecords.reduce((sum, record) => {
                  const premium = record.documentDetails?.member_premium || 
                                 record.documentDetails?.pricing_member_premium || 
                                 0;
                  return sum + Number(premium);
                }, 0);
                
                // Fetch real wallet balance for this user
                let fetchedAvailableBalance = 0;
                try {
                  const walletInfo = user?.walletInfo;
                  if (walletInfo?.hash && walletInfo?.user_wallet_id) {
                    const balanceRes = await getWalletBalance({
                      hash: walletInfo.hash,
                      user_wallet_id: walletInfo.user_wallet_id,
                    });
                    fetchedAvailableBalance = balanceRes?.data?.result?.balance || 0;
                  }
                } catch (balanceErr) {
                  console.warn("⚠️ Could not fetch wallet balance:", balanceErr);
                }

                const validationDataPayload = {
                  successLog: mappedRecords,
                  totalPremium: calculatedTotalPremium || documentDetails.total_premium || 0,
                  availableBalance: fetchedAvailableBalance,
                  errorCount: 0,
                  errorFile: null,
                  successCount: mappedRecords.length,
                  message: `Loaded from quotation ${quotationData.quotation_number}`,
                  partnership: null,
                };
                
                console.log("🚀 Dispatching saveValidationData with:", validationDataPayload);
                
                // Save validation data to Redux
                dispatch(saveValidationData(validationDataPayload));

                // Restore per-record addon state from saved bulk_records
                const restoredSelectedServices = {};
                const restoredAdditionalCost = {};
                const restoredIndividualAddonCosts = {};

                mappedRecords.forEach((record) => {
                  const recordId = record.record_index;
                  const addons = record.addons || [];
                  if (addons.length > 0) {
                    restoredSelectedServices[recordId] = addons.map((a) => ({
                      value: a.service_id,
                      add_on_type: a.add_on_type,
                      addon_coa_value: a.addon_coa_value,
                      addon_coi_value: a.addon_coi_value,
                      addon_value_type: a.addon_value_type,
                      label: a.service_id,
                    }));
                    restoredAdditionalCost[recordId] = Number(record.addonCost) || 0;
                    restoredIndividualAddonCosts[recordId] = {};
                    addons.forEach((a) => {
                      const key = `${a.service_id}+${(a.add_on_type || '').toLowerCase()}`;
                      restoredIndividualAddonCosts[recordId][key] = Number(a.addon_amount) || 0;
                    });
                  }
                });

                if (Object.keys(restoredSelectedServices).length > 0) {
                  console.log("🧩 Restoring addon state from quotation:", restoredSelectedServices);
                  dispatch(saveAddonData({
                    selectedServices: restoredSelectedServices,
                    additionalCost: restoredAdditionalCost,
                    individualAddonCosts: restoredIndividualAddonCosts,
                  }));
                }

                // Restore discount data if any records had discount applied when saved
                const recordsWithDiscount = mappedRecords.filter(r => r.discount && r.discount.percentage > 0);
                if (recordsWithDiscount.length > 0) {
                  const restoredDiscountPct = recordsWithDiscount[0].discount.percentage;
                  console.log("🔖 Restoring discount from quotation:", restoredDiscountPct, "%");
                  dispatch(saveDiscountData({
                    discountPercentage: restoredDiscountPct,
                    discountedRecords: mappedRecords,
                  }));
                } else {
                  // No discount was applied — clear any stale discount data
                  dispatch(clearDiscountData());
                }
                
                // toast.success(`Loaded bulk quotation ${quotationData.quotation_number} with ${mappedRecords.length} records. You can re-upload a new file or proceed with existing data.`);
                
                // Don't auto-navigate - let user stay on review page
                // They can either:
                // 1. Upload a new file to replace the data
                // 2. Click a button to proceed with existing data
              } else {
                toast.warning("No records found in this quotation");
              }
            } else {
              toast.error("This quotation is not a bulk quotation");
              navigate('/sales/policies/quotations');
            }
          }
        } catch (error) {
          console.error("❌ Error loading quotation:", error);
          toast.error("Failed to load quotation data");
        } finally {
          setLoadingQuotation(false);
        }
      }
    };

    loadQuotationData();
  }, [quotation_id, fromQuotation, dispatch, navigate]);

  // Clear discount whenever file is changed (new upload)
  useEffect(() => {
    if (hasFileUploaded) {
      dispatch(clearDiscountData());
    }
  }, [hasFileUploaded, dispatch]);

  const handleSampleDownload = (type) => {
    if (type === "BULK") {

      const companyAndSampleFileMap = {
        // 11: "sample_bulk_upload_sheet_CARE.xlsx", // CARE
        // 11: "sample_bulk_upload_sheet_GC.xlsx", // GC/FGI
        // 11: "sample_bulk_upload_sheet_RGIC.xlsx", // RGIC
        11: "sample_bulk_upload_sheet.xlsx", // TATA AIG  (one for all)
      };


      

      const fileUrl = `/${companyAndSampleFileMap[11]}` || "/Bulk_Document_File.xlsx";
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", `${companyAndSampleFileMap[11] || "Bulk_Document_File.xlsx"}`);
      document.body.appendChild(link);
      link.click();
    } else {
      const fileUrl = "/sample_bulk_upload_FGI.xlsx";
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", "sample_bulk_upload_FGI.xlsx");
      document.body.appendChild(link);
      link.click();
    }
  };

  const submitBulkForm = async (data) => {
    if (!data.fileName) {
      toast.error("Invalid file name. Please upload a valid Excel file.");
      return;
    }
    const fileExtension = data.fileName.split(".").pop().toLowerCase();
    if (fileExtension !== "xls" && fileExtension !== "xlsx") {
      toast.error("Please upload an Excel file (.xls or .xlsx).");
      return;
    }


    // Validate that company and type are selected
    if (!selectedCompany || !selectedPolicyType) {
      toast.error("Company and Policy Type are required for validation!");
      return;
    }
    console.log("User Info:", user);
    const payload =
      role_key === "SALES_MANAGER"
        ? {
            bulkUpload: data.fileName,
            partnership_id: user?.partnerInfo?.partnership_id,
            role_id: role_id,
            created_by_role: role_name,
            company_id: selectedCompany, // Add company_id
            type_id: selectedPolicyType, // Add type_id
          }
        : {
            bulkUpload: data.fileName,
            role_id: role_id,
            created_by_role: role_name,
            company_id: selectedCompany, // Add company_id
            type_id: selectedPolicyType, // Add type_id
          };

    console.log("📤 Validation Payload:", payload);

    setIsProcessingSubmit(true);

    await validateBulkDocuments(payload)
      .then((res) => {
        if (res?.status === 200) {
          const {
            availableBalance,
            totalPremium,
            errorCount,
            errorFile,
            successCount,
            message,
            status,
            successLog,
            partnership,
          } = res?.data?.result;

          // Store the uploaded Excel file URL in Redux
          dispatch(saveBulkFileUrl(data.fileName));

          // Ensure every record has a stable unique record_index so addon keying works correctly
          const indexedSuccessLog = (successLog || []).map((record, idx) => ({
            ...record,
            record_index: record.record_index || record.id || record.proposer?.proposer_id || (idx + 1),
          }));

          // Store the entire validation response in Redux
          dispatch(saveValidationData({
            successLog: indexedSuccessLog,
            totalPremium: totalPremium || 0,
            availableBalance: availableBalance || 0,
            errorCount: errorCount || 0,
            errorFile: errorFile || null,
            successCount: successCount || 0,
            message: message || '',
            partnership: partnership || null,
          }));

                  // Store the entire response for success screen
                  setValidationResponse(res?.data?.result);

                  // ✅ STRICT VALIDATION: Only proceed if ALL documents are successful (errorCount = 0)
                  if (status === "success" && successCount >= 1 && errorCount === 0) {
                    // Navigate to a dedicated success page so the UI is shown on a new route
                    // validation data is already saved to Redux via saveValidationData above
                    try {
                      navigate('/documents/bulk/success',{state:{user: userData}});
                    } catch (e) {
                      // Fallback to full page redirect if router navigation fails for any reason
                      window.location.href = '/documents/bulk/success';
                    }
                    // Call parent callback to transition to Step 2 if provided
                    if (onValidationSuccess) {
                      onValidationSuccess(res?.data?.result);
                    }
          } else {
            // ✅ Show error/failure message - Block navigation if ANY document failed
            const messageBody = `
              <div style="text-align: left; font-family: 'Poppins', sans-serif;">
                <div style="margin-bottom: 20px;">
              
                  <hr style="margin: 15px 0;" />
                  <p style="margin: 10px 0;"><strong style="color: #28a745;">✓ Successful Records:</strong> <span style="color: #28a745; font-size: 18px; font-weight: bold;">${successCount || 0}</span></p>
                  <p style="margin: 10px 0;"><strong style="color: #dc3545;">✗ Failed Records:</strong> <span style="color: #dc3545; font-size: 18px; font-weight: bold;">${errorCount || 0}</span></p>
                  ${errorFile ? `
                    <div style="margin-top: 15px; padding: 12px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px;">
                      <p style="margin: 5px 0 10px 0; color: #856404; font-size: 13px; font-weight: 600;">
                        <i class="fa fa-download" style="margin-right: 5px;"></i>
                        Download Error File to View Failed Records
                      </p>
                      <a 
                        href="${errorFile}" 
                        target="_blank" 
                        download="bulk_errors_${new Date().getTime()}.csv" 
                        rel="noopener noreferrer"
                        style="display: inline-block; padding: 8px 16px; background-color: #dc3545; color: white; text-decoration: none; border-radius: 4px; font-size: 13px;"
                      >
                        <i class="fa fa-download" style="margin-right: 6px;"></i> Download Error File
                      </a>
                    </div>
                  ` : ''}
                  <hr style="margin: 15px 0;" />
                
                </div>
                ${message ? `<p style="font-size: 13px; color: #666; margin-top: 15px; font-style: italic;">${message}</p>` : ''}
              </div>`;

            SweetAlert.fire({
              title: "Validation Failed",
              html: messageBody,
              icon: "error",
              confirmButtonText: "OK",
              confirmButtonColor: "#d33",
            });
            setFileUrl(""); // Clear file URL
            setFileUploadKey(prev => prev + 1); // Force re-render
            setValue("fileName", ""); // Clear form field value
            reset();
            setShowSuccessScreen(false);
            setIsProcessingSubmit(false);
          }
        }
      })
      .catch((err) => {
        setIsProcessingSubmit(false);
        setFileUrl(""); // Clear file URL
        setFileUploadKey(prev => prev + 1); // Force re-render
        setValue("fileName", ""); // Clear form field value
        reset();
        toast.error(err.response?.data?.error || "Something went wrong");
      });
  };

  return (
    <>
      {loadingQuotation ? (
        <Row>
          <Col xl="12" className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Loading quotation data...</p>
          </Col>
        </Row>
      ) : showSuccessScreen && validationResponse ? (
        <BulkDocumentSuccess
          validationData={validationResponse}
          handleBackClick={() => {
            setShowSuccessScreen(false);
            setValidationResponse(null);
            setFileUrl(""); // Clear file URL
            setFileUploadKey(prev => prev + 1); // Force re-render
            setValue("fileName", ""); // Clear form field value
            reset();
          }}
        />
      ) : (
        <Row>
          {/* File Upload Section */}
          {selectedBulkUpload == "yes" ? (
            <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginBottom: "15px" }}>
              <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)' }}>
                <CardBody style={{ padding: '30px' }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #eaeaea', paddingBottom: '15px', marginBottom: '25px' }}>
                    <h5 style={{ margin: 0, fontWeight: '600', color: '#2d3748', fontSize: '20px' }}>
                      <i className="fa fa-cloud-upload" style={{ marginRight: '10px', color: '#3182ce' }}></i>
                      Add Bulk Document
                    </h5>
                  </div>

                  <Form className="needs-validation" onSubmit={handleSubmit(submitBulkForm)}>
                    <Row style={{ alignItems: 'center' }}>
                      <Col xl="7" md="7">
                        <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '10px', border: '1px dashed #cbd5e0' }}>
                          <FormGroup style={{ margin: 0 }}>
                            <FileUploadComponent
                              key={fileUploadKey}
                              add={!hasFileUploaded}
                              register={register}
                              name="fileName"
                              setValue={setValue}
                              trigger={trigger}
                              getValues={getValues}
                              data={fileUrl}
                              xlsxOnly={true}
                              setLoading={setIsProcessingSubmit}
                            />
                            <span style={{ color: "#e53e3e", fontSize: "13px", marginTop: "8px", display: "block", textAlign: "center", fontWeight: "500" }}>
                              {errors.fileName && "File is required"}
                            </span>
                          </FormGroup>
                        </div>
                      </Col>

                      <Col xl="5" md="5" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '20px', paddingLeft: '30px' }}>
                        <div style={{ padding: '20px', background: '#ebf8ff', borderRadius: '10px', border: '1px solid #bee3f8' }}>
                          <h6 style={{ color: '#2b6cb0', fontWeight: '600', fontSize: '15px', marginBottom: '10px' }}>Need the template?</h6>
                          <p style={{ fontSize: '13px', color: '#4a5568', marginBottom: '15px', lineHeight: '1.5' }}>Download the official Excel template to ensure your data is formatted correctly before uploading.</p>
                          <Button
                            color="primary"
                            outline
                            onClick={() => handleSampleDownload("BULK")}
                            style={{ width: '100%', borderRadius: '8px', padding: '10px', fontSize: '14px', fontWeight: '600', backgroundColor: '#fff', border: '1px solid #3182ce', color: '#3182ce' }}
                          >
                            <i className="fa fa-download" style={{ marginRight: '8px' }}></i> Download Bulk Format
                          </Button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <Button
                            color="primary"
                            type="submit"
                            disabled={isProcessingSubmit}
                            style={{ width: '100%', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '600', background: '#3182ce', border: 'none', boxShadow: '0 4px 6px rgba(49, 130, 206, 0.2)', transition: 'all 0.2s' }}
                          >
                            {isProcessingSubmit ? (
                              <><i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i> Validating...</>
                            ) : (
                              <><i className="fa fa-upload" style={{ marginRight: '8px' }}></i> Upload Document</>
                            )}
                          </Button>

                          {fromQuotation && policyData?.validationData?.successLog?.length > 0 && (
                            <Button
                              color="success"
                              onClick={() => {
                                navigate('/documents/bulk/success', { 
                                  state: { 
                                    fromQuotation: true,
                                    quotation_id: quotation_id 
                                  } 
                                });
                              }}
                              type="button"
                              style={{ width: '100%', borderRadius: '8px', padding: '14px', fontSize: '15px', fontWeight: '600', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
                            >
                              <i className="fa fa-arrow-right" style={{ marginRight: '8px' }}></i> Proceed with Loaded Data
                            </Button>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          ) : (
            <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginBottom: "15px" }}>
              <Card>
                <CardBody className="py-2" style={{ padding: "10px 15px" }}>
                  <div>
                    <h6 style={{ fontSize: "13px", marginBottom: "0" }}>Add Bulk Document</h6>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Bottom Buttons */}
          <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginTop: "15px" }}>
            <Card>
              <CardBody className="py-3 body_with_low_padding d-flex justify-content-between align-items-center">
                <div>
                  <Button
                    color="warning"
                    className="rounded-pill"
                    onClick={handleBackClick}
                    disabled={isProcessingSubmit}
                    style={{ padding: "6px 18px", fontSize: "12px" }}
                  >
                    <span className="mr-2">{"<"}</span> Back
                  </Button>
                </div>
                {selectedBulkUpload === "no" && (
                  <Button
                    color="primary"
                    className="rounded-pill"
                    onClick={() => {
                      dispatch(bulkPolicyReset());
                      toast.success(
                        "Bulk Document Configuration Saved Successfully!",
                      );
                    }}
                    disabled={isProcessingSubmit}
                    style={{ padding: "6px 18px", fontSize: "12px" }}
                  >
                    {isProcessingSubmit ? <Spinner size="sm" /> : null} Submit
                  </Button>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default BulkDocumentReview;
